/**
 * Catálogo de navegação alinhado à estrutura de módulos RH.
 * Itens com `to` já têm rota React; demais abrem o Vanilla em `/legado/#/<id>`.
 */

export type AppNavItem = {
  label: string
  /** Chave Auth.MATRIX / canView */
  matrixKey: string
  /** Rota React interna (se já portado) */
  to?: string
  end?: boolean
  /** Hash do legado, ex. `#/cargos` (default `#/<matrixKey>`) */
  legacyHash?: string
  group?: string
}

/** Módulos já portados para React. */
export const REACT_ROUTES: Record<string, { to: string; end?: boolean }> = {
  dashboard: { to: '/app', end: true },
  'command-center': { to: '/app/command-center' },
  recrutamento: { to: '/app/recrutamento' },
  'plano-contratacao': { to: '/app/plano-contratacao' },
  colaboradores: { to: '/app/rh' },
  timeline: { to: '/app/timeline' },
  'perfil-dna': { to: '/app/perfil-dna' },
  aniversariantes: { to: '/app/aniversariantes' },
  cargos: { to: '/app/cargos' },
  empresas: { to: '/app/empresas' },
  'organograma-vivo': { to: '/app/organograma' },
  competencias: { to: '/app/competencias' },
  'central-integracao': { to: '/app/central-integracao' },
  desenvolvimento: { to: '/app/treinamentos' },
  'avd-desempenho': { to: '/app/td/avd' },
  'feedback-mensal': { to: '/app/td/feedback' },
  pdi: { to: '/app/td/pdi' },
  departamento: { to: '/app/dp' },
  'dp-admissao': { to: '/app/dp/admissao' },
  'dp-ferias': { to: '/app/dp/ferias' },
  'dp-rescisao': { to: '/app/dp/rescisao' },
  'dp-beneficios': { to: '/app/dp/beneficios' },
  'dp-documentos': { to: '/app/documentos' },
  'assinatura-digital': { to: '/app/dp/assinatura' },
  'historico-funcional': { to: '/app/dp/historico' },
  dpc: { to: '/app/dp/rotina' },
  'dp-avaliacao-exp': { to: '/app/dp/experiencia' },
  nr01: { to: '/app/dp/nr01' },
  cipa: { to: '/app/sst/cipa' },
  ergonomia: { to: '/app/sst/ergonomia' },
  'risco-trabalhista': { to: '/app/dp/risco' },
  'epi-fardamento': { to: '/app/dp/epi' },
  'laudo-tecnico': { to: '/app/dp/laudos' },
  'dp-massa': { to: '/app/dp/massa' },
  gestor: { to: '/app/gestor' },
  portal: { to: '/app/colaborador' },
  analytics: { to: '/app/analytics' },
  'alertas-corporativos': { to: '/app/alertas' },
  relatorios: { to: '/app/relatorios' },
  documentos: { to: '/app/documentos' },
  ouvidoria: { to: '/app/ouvidoria' },
  'knowledge-base': { to: '/app/knowledge-base' },
  'reunioes-lideranca': { to: '/app/reunioes' },
  'calendario-corporativo': { to: '/app/calendario' },
  indicadores: { to: '/app/indicadores' },
  'ia-rh': { to: '/app/ia-rh' },
  automacao: { to: '/app/automacao' },
  'onboarding-shadow': { to: '/app/onboarding-shadow' },
  workflows: { to: '/app/workflows' },
  usuarios: { to: '/app/usuarios' },
  integracoes: { to: '/app/integracoes' },
  'rede-feed': { to: '/app/rede' },
  'tv-corporativa': { to: '/app/tv' },
  'vibracao-corporativa': { to: '/app/clima' },
  'acoes-internas': { to: '/app/clima/acoes' },
  'whatsapp-inbox': { to: '/app/whatsapp' },
}

/**
 * Lista plana (ordem do menu). Filtrar com canView no shell.
 * Grupos oficiais RH primeiro; demais = apoio / admin.
 */
export const APP_NAV_CATALOG: AppNavItem[] = [
  { label: 'Início', matrixKey: 'dashboard', to: '/app', end: true, group: 'Painel' },

  // ── 1º Departamento Pessoal ────────────────────────────────
  {
    label: 'Cadastro e gestão de colaboradores',
    matrixKey: 'colaboradores',
    to: '/app/rh',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Admissão',
    matrixKey: 'dp-admissao',
    to: '/app/dp/admissao',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Rescisão',
    matrixKey: 'dp-rescisao',
    to: '/app/dp/rescisao',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Férias',
    matrixKey: 'dp-ferias',
    to: '/app/dp/ferias',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Cargos e Salários',
    matrixKey: 'cargos',
    to: '/app/cargos',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Folha de pagamento',
    matrixKey: 'departamento',
    to: '/app/dp',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Controle de documentos',
    matrixKey: 'dp-documentos',
    to: '/app/documentos',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Gestão de benefícios',
    matrixKey: 'dp-beneficios',
    to: '/app/dp/beneficios',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Assinatura digital',
    matrixKey: 'assinatura-digital',
    to: '/app/dp/assinatura',
    group: 'Departamento Pessoal',
  },
  {
    label: 'Histórico funcional',
    matrixKey: 'timeline',
    to: '/app/dp/historico',
    group: 'Departamento Pessoal',
  },

  // ── 2º Recrutamento e Seleção ──────────────────────────────
  {
    label: 'Descritivo de Cargos',
    matrixKey: 'cargos',
    to: '/app/cargos',
    group: 'Recrutamento e Seleção',
  },
  {
    label: 'Recrutamento',
    matrixKey: 'recrutamento',
    to: '/app/recrutamento',
    group: 'Recrutamento e Seleção',
  },
  {
    label: 'Seleção',
    matrixKey: 'recrutamento',
    to: '/app/recrutamento',
    group: 'Recrutamento e Seleção',
  },
  {
    label: 'Integração',
    matrixKey: 'central-integracao',
    to: '/app/central-integracao',
    group: 'Recrutamento e Seleção',
  },

  // ── 3º Treinamento e Desenvolvimento ───────────────────────
  {
    label: 'Avaliação de Desempenho',
    matrixKey: 'desenvolvimento',
    to: '/app/td/avd',
    group: 'Treinamento e Desenvolvimento',
  },
  {
    label: 'Avaliação de Feedback',
    matrixKey: 'desenvolvimento',
    to: '/app/td/feedback',
    group: 'Treinamento e Desenvolvimento',
  },
  {
    label: 'Programa de Desenvolvimento Individual',
    matrixKey: 'desenvolvimento',
    to: '/app/td/pdi',
    group: 'Treinamento e Desenvolvimento',
  },

  // ── 4º Clima e Cultura ─────────────────────────────────────
  {
    label: 'Pesquisa de Clima',
    matrixKey: 'vibracao-corporativa',
    to: '/app/clima',
    group: 'Clima e Cultura',
  },
  {
    label: 'Ações Internas',
    matrixKey: 'vibracao-corporativa',
    to: '/app/clima/acoes',
    group: 'Clima e Cultura',
  },

  // ── 5º Saúde e Segurança ───────────────────────────────────
  {
    label: 'CIPA',
    matrixKey: 'nr01',
    to: '/app/sst/cipa',
    group: 'Saúde e Segurança',
  },
  {
    label: 'EPIs',
    matrixKey: 'epi-fardamento',
    to: '/app/dp/epi',
    group: 'Saúde e Segurança',
  },
  {
    label: 'Ergonomia',
    matrixKey: 'laudo-tecnico',
    to: '/app/sst/ergonomia',
    group: 'Saúde e Segurança',
  },

  // ── Cadastro / apoio ───────────────────────────────────────
  { label: 'Empresas', matrixKey: 'empresas', to: '/app/empresas', group: 'Cadastro' },
  { label: 'Organograma', matrixKey: 'organograma-vivo', to: '/app/organograma', group: 'Cadastro' },
  { label: 'Competências (tags)', matrixKey: 'competencias', to: '/app/competencias', group: 'Cadastro' },
  { label: 'Aniversariantes', matrixKey: 'aniversariantes', to: '/app/aniversariantes', group: 'Cadastro' },
  { label: 'Contratação', matrixKey: 'plano-contratacao', to: '/app/plano-contratacao', group: 'Cadastro' },

  // ── Apoio ──────────────────────────────────────────────────
  { label: 'Ouvidoria', matrixKey: 'ouvidoria', to: '/app/ouvidoria', group: 'Apoio' },
  { label: 'Base de conhecimento', matrixKey: 'knowledge-base', to: '/app/knowledge-base', group: 'Apoio' },
  { label: 'Gestão de equipes', matrixKey: 'gestor', to: '/app/gestor', group: 'Apoio' },
  { label: 'Alertas', matrixKey: 'alertas-corporativos', to: '/app/alertas', group: 'Apoio' },
  { label: 'Relatórios', matrixKey: 'relatorios', to: '/app/relatorios', group: 'Apoio' },
  { label: 'Analytics', matrixKey: 'analytics', to: '/app/analytics', group: 'Apoio' },
  { label: 'Rede', matrixKey: 'rede-feed', to: '/app/rede', group: 'Apoio' },
  { label: 'Aprovações', matrixKey: 'workflows', to: '/app/workflows', group: 'Apoio' },

  // ── Admin ──────────────────────────────────────────────────
  { label: 'Usuários', matrixKey: 'usuarios', to: '/app/usuarios', group: 'Admin' },
  { label: 'Integrações', matrixKey: 'integracoes', to: '/app/integracoes', group: 'Admin' },

  { label: 'Meu painel', matrixKey: 'portal', to: '/app/colaborador', group: 'Portal' },
]

/** Resolve destino React se existir. */
export function resolveNavItem(item: AppNavItem): AppNavItem {
  if (item.to) return item
  const react = REACT_ROUTES[item.matrixKey]
  if (react) return { ...item, to: react.to, end: react.end }
  return item
}
